import { LatLngExpression } from 'leaflet'
import { memo, useEffect, useState } from 'react'
import { Polyline, Tooltip, useMap, useMapEvent } from 'react-leaflet'
import { getPositionName } from 'src/utils/function'
import { IPosition } from '../../../../../generated-types'
import { grayedOpacity, mapOptions, minZoom, opacity } from '../constraints'
import { useShowHiddenPositions } from '../ShowHiddenPositionsContext'
import { getLineHitAreaWeight } from '../utils'
import { useMooringsCtx } from 'src/hooks/context-hooks'

interface IInnerMooringLineProps {
  position: IPosition
  navigateToPosition: (positionId: number) => void
  showCriticalPositions?: boolean
}

export const InnerMooringLine: React.FC<IInnerMooringLineProps> = memo(
  ({ position, navigateToPosition, showCriticalPositions = false }) => {
    const showHiddenPositions = useShowHiddenPositions()
    const map = useMap()
    const [zoomLevel, setZoomLevel] = useState(Math.round(map.getZoom()))

    useEffect(() => {
      setZoomLevel(Math.floor(map.getZoom()))
    }, [map.getZoom()])

    const handleZoomEnd = event => {
      const newZoomLevel = event.target.getZoom()
      setZoomLevel(Math.round(newZoomLevel))
    }
    useMapEvent('zoomend', handleZoomEnd)
    const { isCritical } = useMooringsCtx()
    const shouldHighlightCritical = !!(isCritical || showCriticalPositions)
    // Hidden positions are drawn greyed out when "show hidden" is on, so they can be found.
    if (position.isHidden && !showHiddenPositions) return <></>
    if (!position.connectedPositions[0]) return <></>
    if (position.isHidden) position = { ...position, isTransparent: true }

    let latLongs = [] as LatLngExpression[]

    if (position.connectedPositions[0] && position.connectedPositions[1]) {
      latLongs = [
        [position.connectedPositions[0].latitude, position.connectedPositions[0].longitude],
        [position.connectedPositions[1].latitude, position.connectedPositions[1].longitude],
      ]
    }

    const pathOptions = {
      weight: 4,
      simpleline: 1.6,
      opacity: position.isTransparent ? grayedOpacity : opacity,
      fillOpacity: position.isTransparent ? grayedOpacity : opacity,
      color:
        position.deviationsCount > 0
          ? opacity !== 1.0
            ? mapOptions.mooringColor
            : mapOptions.deviationColor
          : mapOptions.innerMooringColor,
      pane: position.isWorkAssignmentPosition ? 'innerMooringLineUpdated' : 'innerMooringLine',
    }

    const toolTip = `${mapOptions.lang.innerMooringLine} ${getPositionName(position)}`
    const baseWeight = zoomLevel - minZoom <= 0 ? 1 : zoomLevel - minZoom
    pathOptions.weight = position.isCritical && shouldHighlightCritical ? baseWeight + 2 : baseWeight

    return latLongs.length > 0 ? (
      <>
        <Polyline
          key={position.id}
          positions={latLongs as LatLngExpression[]}
          pathOptions={pathOptions}
          pane={pathOptions.pane}
          interactive={false}
        />
        <Polyline
          positions={latLongs as LatLngExpression[]}
          pathOptions={{ ...pathOptions, weight: getLineHitAreaWeight(pathOptions.weight), opacity: 0 }}
          pane={pathOptions.pane}
          eventHandlers={{
            click: () => {
              navigateToPosition(position.id)
            },
          }}
        >
          <Tooltip sticky>
            <strong>{toolTip}</strong>
          </Tooltip>
        </Polyline>
      </>
    ) : null
  },
)
